import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  rememberme: z.boolean().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginForm) {
    try {
      setError('');
      await login(data.email, data.password, data.rememberme || false);
      navigate('/');
    } catch (err: unknown) {
      const resp = err as { response?: { data?: { error?: string } } };
      setError(resp.response?.data?.error || 'Invalid email or password');
    }
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/assets/logo.png" alt="Connectfy" className="h-12 mx-auto mb-4" />
          <CardTitle className="text-2xl font-bold">Welcome to Connectfy Portal</CardTitle>
          <CardDescription className="text-[#767676]">
            Connecting Systems
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-[#cc0404] p-3 rounded-[4px] text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                className="rounded-[4px]"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-[#cc0404]">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                className="rounded-[4px]"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-[#cc0404]">{errors.password.message}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                id="rememberme"
                type="checkbox"
                className="rounded border-gray-300"
                {...register('rememberme')}
              />
              <Label htmlFor="rememberme" className="text-sm font-normal text-[#767676]">
                Remember me
              </Label>
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Login'}
            </Button>
            <p className="text-center text-sm text-[#767676]">
              New user?{' '}
              <Link to="/signup" className="text-[#cc0404] hover:underline">
                Create Account
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
